import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  CloudDownload,
  Plus,
} from "lucide-react";
import { useActorStore } from "@/stores/actorStore";
import { useDebounce } from "@/hooks/useDebounce";
import { Actor } from "@/types/actor";
import { ROUTES } from "@/constants/routes";
import SmartPagination from "@/components/ui/smart-pagination";

export default function ActorPage() {
  const navigate = useNavigate();
  const {
    actors,
    pagination,
    isLoading,
    isSyncing,
    fetchActors,
    syncBulk,
    setCurrentPage,
    setSelectedActor,
    deleteActor,
  } = useActorStore();

  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [, startTransition] = useTransition();
  const deferredActors = useDeferredValue(actors);

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedActorForDelete, setSelectedActorForDelete] =
    useState<Actor | null>(null);

  // Build query params memoized
  const queryParams = useMemo(() => {
    return {
      page: pagination.page,
      limit: pagination.limit,
      search: debouncedSearch || undefined,
    } as const;
  }, [debouncedSearch, pagination.page, pagination.limit]);

  useEffect(() => {
    startTransition(() => {
      fetchActors({ ...queryParams });
    });
  }, [queryParams, fetchActors]);

  // Handle pagination
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  // Handle sync
  const handleSyncAll = async () => {
    await syncBulk();
  };

  // Handle delete
  const handleDelete = async () => {
    if (selectedActorForDelete) {
      await deleteActor(selectedActorForDelete._id);
      setIsDeleteModalOpen(false);
      setSelectedActorForDelete(null);
    }
  };

  const getSyncStatusBadge = (actor: Actor) => {
    if (actor.seoTitle || actor.metaDescription) {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          Đã có SEO
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
        Chưa có SEO
      </Badge>
    );
  };

  const actorsList = useMemo(() => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Đang tải...</p>
        </div>
      );
    }
    if (deferredActors.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">
            Không có diễn viên nào
          </div>
          <div className="text-gray-500">
            Nhấn "Sync từ External" để đồng bộ dữ liệu
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 dark:bg-gray-800/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Hình ảnh
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Tên
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Phiên âm
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Quốc gia
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  SEO Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100">
              {deferredActors.map((actor: Actor) => (
                <tr
                  key={actor._id}
                  className="transition-all hover:bg-gray-50/60"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {actor.image_url ? (
                      <img
                        src={actor.image_url}
                        alt={actor.name}
                        className="w-12 h-12 object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-sm">
                        N/A
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{actor.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {actor.nameRomanized || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                    {actor.url}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {actor.countryName || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getSyncStatusBadge(actor)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedActor(actor);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          navigate(
                            ROUTES.EDIT_ACTOR.replace(":id", actor._id)
                          )
                        }
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          setSelectedActorForDelete(actor);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="mt-4">
            <SmartPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </>
    );
  }, [
    isLoading,
    deferredActors,
    pagination,
    handlePageChange,
    navigate,
    setSelectedActor,
  ]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
              Quản lý Diễn viên
            </h1>
            <p className="text-gray-500 mt-1">
              Danh sách diễn viên và thông tin SEO
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-blue-50 text-blue-700 px-3 py-1">
              {pagination.total} diễn viên
            </Badge>
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={isSyncing}
            >
              <CloudDownload className="w-4 h-4 mr-2" />
              {isSyncing ? "Đang sync..." : "Sync từ External"}
            </Button>
            <Button
              onClick={() => navigate(ROUTES.CREATE_ACTOR)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Thêm diễn viên
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Bộ lọc và tìm kiếm</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Tìm kiếm theo tên..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Làm mới
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">{actorsList}</CardContent>
        </Card>

        {/* Delete Modal */}
        {isDeleteModalOpen && selectedActorForDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Xác nhận xóa</h3>
              <p className="text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa diễn viên "
                {selectedActorForDelete.name}"?
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedActorForDelete(null);
                  }}
                >
                  Hủy
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Xóa
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
